import { CheckCircleFilled, CloseCircleFilled, MoreOutlined } from "@ant-design/icons";
import { Dropdown, Menu, Modal, Spin, Tag } from "antd";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { getErrorMessage } from "src/common/errors";
import {
  firstUpperCase, isEmptyString,
  isNullOrEmptyString,
  isNullOrUndefined,
  isObject,
} from "../../../../common/utils";
import { formatUtcDateToLocal, parseDate } from "../../../../common/utils/TimeUtils";
import { LinkSpan } from "../../../../components/linkSpan";
import { sendUserGroupEvent, UserGroupAction } from "../../../../components/matomoClient";
import { IndividualInclusionMemberToolTip } from "../../../../components/individualInclusionMemberToolTip";
import { TextWithSubTitle } from "../../../../components/textWithSubTitle";
import { UserGroupActions } from "../../model";
import { fromTabQueryParam } from "../../userGroupDetails/model";
import { listUserGroupTabTypes } from "../model";
import * as service from "../service";
import "./index.scss";

export function recordStringValue(record) {
  if (isObject(record)) {
    let recordValue = "";
    for (let key in record) {
      recordValue = `${recordValue}${record[key]} `;
    }
    if (!isNullOrEmptyString(recordValue)) {
      recordValue.trim();
    }
    return recordValue;
  } else {
    return "";
  }
}

async function publishUserGroup(userGroupId, userGroupType) {
  return await service.publishUserGroup(userGroupId, userGroupType);
}

function getTitle(titleMsg) {
  return (
    <>
      <p><b>{titleMsg}</b></p>
    </>
  );
}

function constructContent(isError, errorMsg) {
  return (
    <>
      {
        !isError ? (
          <>
            <p><b>Business Team published successfully</b></p>
          </>
        ) : (
          <>
            <p>Reason: {errorMsg}</p>
          </>
        )
      }
    </>
  );
}

function PublishAction({ record }) {
  const dispatch = useDispatch();
  const [apiCallStatus, setApiCallStatus] = useState(false);

  return (
    <Menu>
      <Menu.Item
        style={{ cursor: apiCallStatus ? "not-allowed" : "pointer" }}
        onClick={async () => {
          try {
            setApiCallStatus(true);
            await publishUserGroup(record.id, record.type);
            dispatch(
              UserGroupActions.setPublishedStatusForUserGroup({
                userGroupId: record.id,
                userGroupTypeId: record.type,
              })
            );
            setApiCallStatus(false);
            Modal.success({
              icon: <CheckCircleFilled/>,
              title: getTitle("Confirm Message"),
              content: constructContent(false),
              className: "simpleModal",
            });
          } catch (error) {
            setApiCallStatus(false);
            Modal.error({
              icon: <CloseCircleFilled/>,
              title: getTitle("Business Team could not be published"),
              content: constructContent(true, getErrorMessage(error))
            });
          }
        }}
      >
        Publish {apiCallStatus ? <Spin size="small" /> : null}
      </Menu.Item>
    </Menu>
  );
}

function StatusOption({ record }) {

  return (
    <div >
      <div >
        {record.isAllowedToPublish ? (
          <Dropdown
            data-testid="dropdown-publish"
            overlay={
              <PublishAction
                record={record}
              />
            }
            trigger={["hover"]}
          >
            <MoreOutlined role="more-button" style={{ color: "#000000", fontSize: "22px", cursor: "pointer" }} />
          </Dropdown>
        ) : (
          <MoreOutlined role="more-button" style={{ color: "#CECECE", fontSize: "22px", cursor: "not-allowed" }} />
        )}
      </div>
    </div>
  );
}

export function userGroupListColumns(columnName, viewMyUserGroups, isViewPermissionMember, isIndividualInclusionNamingEnabled) {
  const columns =
    isNullOrUndefined(columnName) || isEmptyString(columnName)
      ? []
      : [
        {
          title: `${columnName} name`,
          dataIndex: "name",
          key: "name",
          searchEnabled: true,
          sortEnabled: true,
          render: (userGroupName, record) => {
            let hasIndividualInclusionMembers = record.hasIndividualInclusionMembers;
            const areMembersAvailable = record.noOfMembers > 0;
            let fromTab;
            if (viewMyUserGroups === true) {
              fromTab = listUserGroupTabTypes.MyList;
            } else {
              fromTab = listUserGroupTabTypes.AllList;
            }
            return (
              <>
                {viewMyUserGroups === true || isViewPermissionMember === true ? (
                  <LinkGroupName
                    id={record.uniqueName}
                    userGroupName={userGroupName}
                    columnName={columnName}
                    areMembersAvailable={areMembersAvailable}
                    fromTab={fromTab}
                    hasIndividualInclusionMembers={hasIndividualInclusionMembers}
                    isIndividualInclusionNamingEnabled={isIndividualInclusionNamingEnabled}
                  />
                ) : (
                  <GroupName
                    id={userGroupName}
                    titles={[userGroupName, ""]}
                    hasIndividualInclusionMembers={hasIndividualInclusionMembers}
                    isIndividualInclusionNamingEnabled={isIndividualInclusionNamingEnabled}
                  />
                )}
              </>
            );
          },
        },
        {
          title: `${columnName} owners`,
          dataIndex: "owners",
          key: "owners",
          render: (owners) => {
            return (
              <div className="owners-list">
                {owners?.map((owner) => {
                  return (
                    <TextWithSubTitle
                      key={owner?.name}
                      titles={[owner?.name, owner?.jobProfile]}
                    />
                  );
                })}
              </div>
            );
          },
          searchEnabled: true,
          onFilter: (value, record) => {
            let finalString = "";
            record.owners?.forEach((owner) => {
              finalString = `${finalString}${recordStringValue(owner)}|`;
            });
            return finalString ? finalString.toLowerCase().includes(value.toLowerCase()) : "";
          },
        },
        {
          title: "TW business function",
          dataIndex: "functionalGroup",
          key: "functionalGroup",
          sortEnabled: true,
          searchEnabled: true,
        },
        {
          title: "Default access scope",
          dataIndex: "scope",
          key: "scope",
          render: (scope) => {
            const btScope = scope?.name ?? "";
            return firstUpperCase(btScope);
          },
          sortEnabled: true,
          searchEnabled: true,
          onFilter: (value, record) => {
            let recordValue = record.scope?.name;
            return recordValue ? recordValue.toLowerCase().includes(value.toLowerCase()) : "";
          },
        },
        {
          title: "Members",
          dataIndex: "noOfMembers",
          key: "noOfMembers",
          sortEnabled: true,
          sorter: (recordA, recordB) => {
            return recordA.noOfMembers - recordB.noOfMembers;
          },
        },
        {
          title: "Updated by/on",
          dataIndex: "lastUpdated",
          key: "lastUpdated",
          render: (lastUpdated) => {
            const lastUpdatedDate =
              isNullOrUndefined(lastUpdated?.time) || isEmptyString(lastUpdated?.time)
                ? ""
                : formatUtcDateToLocal(parseDate(lastUpdated?.time), "DD MMM YYYY");
            return <TextWithSubTitle titles={[lastUpdated?.name, lastUpdatedDate]} />;
          },
          sortEnabled: true,
          sorter: (recordA, recordB) => {
            const recordALastUpdatedTime = recordA.lastUpdated.time;
            const recordBLastUpdatedTime = recordB.lastUpdated.time;
            return recordALastUpdatedTime.localeCompare(recordBLastUpdatedTime);
          },
          searchEnabled: true,
          onFilter: (value, record) => {
            let recordValue = record.lastUpdated?.name;
            return recordValue ? recordValue.toLowerCase().includes(value.toLowerCase()) : "";
          },
        },
        {
          title: "Description",
          dataIndex: "description",
          key: "description",
          sortEnabled: true,
          searchEnabled: true,
          width: "350px"
        },
        {
          title: "Status",
          dataIndex: "isPublished",
          key: "isPublished",
          render: (isPublished, record) => {
            return (
              <div className="flex-container">
                <div>
                  {isPublished ? (
                    <Tag className={"status-tag"} color="success">
                      Published
                    </Tag>
                  ) : (
                    <Tag className={"status-tag"} color="default">
                      Draft
                    </Tag>
                  )}
                </div>
                <StatusOption record={record} />
              </div>
            );
          },
          filters: [
            {
              "text": "Draft",
              "value": false
            },
            {
              "text": "Published",
              "value": true
            }
          ],
          filterMultiple: false,
          sortEnabled: false,
          searchEnabled: false,
          filterEnabled: true,
        },
      ];
  columns.map((column) => {
    if (isNullOrUndefined(column.sortEnabled)) {
      column.sortEnabled = false;
    }
    if (isNullOrUndefined(column.searchEnabled)) {
      column.searchEnabled = false;
    }
    if (isNullOrUndefined(column.width)) {
      column.width = "auto";
    }
    return column;
  });
  return columns;
}

const GroupName = ({
  id,
  titles,
  hasIndividualInclusionMembers,
  isIndividualInclusionNamingEnabled,
}) => {
  return (
    <div>
      <span key={id} className="user-group-name-non-link">
        {titles}
      </span>
      {hasIndividualInclusionMembers === true ? (
        <IndividualInclusionMemberToolTip
          title={
            isIndividualInclusionNamingEnabled
              ? "Contains individual inclusions"
              : "Contains special members"
          }
        />
      ) : null}
    </div>
  );
};

const LinkGroupName = ({
  id,
  userGroupName,
  columnName,
  areMembersAvailable,
  fromTab,
  hasIndividualInclusionMembers,
  isIndividualInclusionNamingEnabled
}) => {
  return (
    <div>
      <LinkSpan
        to={`${window.location.pathname}/${id}?${fromTabQueryParam}=${fromTab}`}
        keyId={id}
        text={userGroupName}
        matomoFn={() => {
          sendUserGroupEvent(UserGroupAction.UserGroupClick(columnName), userGroupName);
        }}
      />
      {hasIndividualInclusionMembers === true ? (
        <IndividualInclusionMemberToolTip
          title={
            isIndividualInclusionNamingEnabled
              ? "Contains individual inclusions"
              : "Contains special members"
          }
        />
      ) : null}
    </div>
  );
};
